import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Send, ArrowLeft, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Messages = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  useEffect(() => {
    fetchConversations();
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, []);

  useEffect(() => {
    if (userId) {
      fetchUserAndMessages(userId);
    } else {
      setSelectedUser(null);
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Poll for new messages every 5 seconds when in a conversation
    if (selectedUser) {
      pollInterval.current = setInterval(() => {
        fetchMessages(selectedUser.id, true);
      }, 5000);
    }
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAndMessages = async (targetUserId) => {
    try {
      const userResponse = await axios.get(`${API}/users/${targetUserId}`);
      setSelectedUser(userResponse.data);
      await fetchMessages(targetUserId);
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Usuario no encontrado');
      navigate('/messages');
    }
  };

  const fetchMessages = async (targetUserId, silent = false) => {
    try {
      const response = await axios.get(`${API}/messages/${targetUserId}`);
      setMessages(response.data);
      if (!silent) fetchConversations(); // Refresh unread counts
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        receiver_id: selectedUser.id,
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar el mensaje');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    navigate(`/messages/${conv.user_id}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-1 sm:px-2 md:px-4 h-[calc(100vh-8rem)] md:h-[calc(100vh-6rem)]" data-testid="messages-container">
      <div className="grid grid-cols-1 md:grid-cols-3 h-full bg-zinc-900/30 border border-zinc-800">
        {/* Conversations List */}
        <div className={`md:border-r border-zinc-800 ${selectedUser ? 'hidden md:block' : ''}`}>
          <div className="p-4 border-b border-zinc-800">
            <h2 className="font-['Cinzel'] text-xl font-semibold text-white">MENSAJES</h2>
          </div>

          <ScrollArea className="h-[calc(100%-4rem)]">
            {loading ? (
              <div className="p-4 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-zinc-800 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-zinc-800 animate-pulse w-24" />
                      <div className="h-3 bg-zinc-800 animate-pulse w-32" />
                    </div>
                  </div>
                ))}
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageCircle className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No tienes conversaciones aún</p>
                <p className="text-zinc-600 text-sm mt-2">
                  Visita el perfil de un artista para enviar un mensaje
                </p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-800/50">
                {conversations.map((conv) => (
                  <button
                    key={conv.user_id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full p-4 flex items-center gap-3 hover:bg-zinc-800/50 transition-colors text-left ${selectedUser?.id === conv.user_id ? 'bg-zinc-800/50' : ''
                      }`}
                    data-testid={`conversation-${conv.user_id}`}
                  >
                    <Avatar className="h-12 w-12 border border-zinc-800">
                      <AvatarImage src={conv.user_avatar} />
                      <AvatarFallback className="bg-zinc-800 text-zinc-400">
                        {conv.user_name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-white truncate">{conv.user_name}</p>
                        <span className="text-xs text-zinc-600">
                          {formatDistanceToNow(new Date(conv.last_message_time), { locale: es, addSuffix: false })}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-500 truncate">{conv.last_message}</p>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="w-5 h-5 bg-rose-500 text-white text-xs flex items-center justify-center rounded-full">
                        {conv.unread_count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className={`md:col-span-2 flex flex-col ${!selectedUser ? 'hidden md:flex' : ''}`}>
          {selectedUser ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
                <button
                  onClick={() => navigate('/messages')}
                  className="md:hidden text-zinc-400 hover:text-white"
                >
                  <ArrowLeft size={20} />
                </button>
                <Avatar className="h-10 w-10 border border-zinc-800">
                  <AvatarImage src={selectedUser.avatar} />
                  <AvatarFallback className="bg-zinc-800 text-zinc-400">
                    {selectedUser.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-white">{selectedUser.name}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">
                    {selectedUser.user_type === 'artist' ? 'Tatuador' : 'Cliente'}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${message.id}`}
                    >
                      <div
                        className={`max-w-[80%] p-4 ${message.sender_id === user.id
                          ? 'bg-zinc-800'
                          : 'bg-zinc-900 border border-zinc-800'
                          }`}
                      >
                        <p className="text-white">{message.content}</p>
                        <p className="text-xs text-zinc-600 mt-2">
                          {formatDistanceToNow(new Date(message.created_at), { addSuffix: true, locale: es })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-zinc-800">
                <div className="flex gap-3">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    className="flex-1 bg-transparent border-zinc-800 focus-visible:ring-0"
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="bg-zinc-100 text-zinc-950 hover:bg-white"
                    data-testid="send-message-btn"
                  >
                    <Send size={18} />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-zinc-800 mx-auto mb-4" />
                <p className="text-zinc-500">Selecciona una conversación</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
